/**
 * NoteFormSheet — create/edit BottomSheet for Onthouden (spec §8.2).
 *
 * - Visible SaveBar footer with the canonical sync state next to the actions.
 * - A leave-with-unsaved-changes guard (react-router useBlocker + confirm,
 *   with a beforeunload fallback) protects a dirty sheet; the device-only
 *   draft ('rust-remember-draft', labelled 'Alleen op dit apparaat') is
 *   flushed before leaving so nothing is lost.
 * - On a failed cloud save the form NEVER resets: values stay intact, an
 *   explicit retry is offered, and no cloud-save copy is ever shown.
 * - Cancelling a dirty sheet asks first; confirming discards the local draft.
 */
import { useEffect, useMemo, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import {
  Banner,
  BottomSheet,
  Button,
  Checkbox,
  ConfirmDestructiveAction,
  SaveBar,
  Select,
  SyncStatus,
  TextArea,
  TextField,
} from '@/components/ui'
import type { AppError, Note, SyncSaveState } from '@/types'
import { NOTE_CATEGORIES } from './categories'
import type { NoteDraft } from './drafts'
import type { NoteFormValues, SaveNoteInput, SaveNoteResult } from './useNotes'

export type NoteFormTarget =
  | { kind: 'new' }
  | { kind: 'edit'; note: Note }
  | { kind: 'draft'; draft: NoteDraft }

type NoteFormSheetProps = {
  open: boolean
  target: NoteFormTarget
  onSave: (input: SaveNoteInput) => Promise<SaveNoteResult>
  /** Called after a confirmed cloud save; the parent closes the sheet. */
  onSaved: () => void
  /** Explicit discard: closes the sheet and removes the device-only draft. */
  onCancel: () => void
  onPersistDraft: (draft: NoteDraft) => void
}

function initialValues(target: NoteFormTarget): NoteFormValues {
  if (target.kind === 'edit') {
    return {
      title: target.note.title,
      content: target.note.content,
      category: target.note.category,
      pinned: target.note.pinned,
      urgent: target.note.urgent,
    }
  }
  if (target.kind === 'draft') {
    return {
      title: target.draft.title,
      content: target.draft.content,
      category: target.draft.category,
      pinned: target.draft.pinned,
      urgent: target.draft.urgent,
    }
  }
  return { title: '', content: '', category: 'Algemeen', pinned: false, urgent: false }
}

export function NoteFormSheet({ open, target, onSave, onSaved, onCancel, onPersistDraft }: NoteFormSheetProps) {
  const noteId = target.kind === 'edit' ? target.note.id : target.kind === 'draft' ? target.draft.noteId : null
  const initial = useMemo(() => initialValues(target), [target])

  const [values, setValues] = useState<NoteFormValues>(initial)
  const [titleError, setTitleError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveState, setSaveState] = useState<SyncSaveState | null>(null)
  const [saveError, setSaveError] = useState<AppError | null>(null)
  const [discardOpen, setDiscardOpen] = useState(false)

  const dirty = useMemo(
    () =>
      values.title !== initial.title ||
      values.content !== initial.content ||
      values.category !== initial.category ||
      values.pinned !== initial.pinned ||
      values.urgent !== initial.urgent,
    [values, initial],
  )

  const buildDraft = (state: SyncSaveState): NoteDraft => ({
    noteId,
    title: values.title.trim(),
    content: values.content.trim(),
    category: values.category,
    pinned: values.pinned,
    urgent: values.urgent,
    state,
    updatedAt: new Date().toISOString(),
  })

  // Debounced device-only draft persistence while editing (survives reloads).
  useEffect(() => {
    if (!open || !dirty) return
    const handle = setTimeout(() => {
      onPersistDraft(
        buildDraft(saveState === 'sync_failed' || saveState === 'sync_queued' ? saveState : 'local_saved'),
      )
    }, 400)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dirty, values, saveState, noteId, onPersistDraft])

  // Browser tab close / reload guard while dirty.
  useEffect(() => {
    if (!open || !dirty) return
    const handler = (event: BeforeUnloadEvent) => {
      onPersistDraft(buildDraft('local_saved'))
      event.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dirty, values, noteId, onPersistDraft])

  // Route-leave guard (react-router v7 data router blocker).
  const blocker = useBlocker(open && dirty)

  const updateValue = (patch: Partial<NoteFormValues>) => {
    setValues((previous) => ({ ...previous, ...patch }))
  }

  const handleSave = async () => {
    if (saving) return
    if (!values.title.trim()) {
      setTitleError('Vul een titel in.')
      return
    }
    setTitleError(null)
    setSaveError(null)
    setSaving(true)
    setSaveState('syncing')
    const result = await onSave({ noteId, values })
    setSaving(false)
    if (result.ok) {
      setSaveState('cloud_saved')
      onSaved()
      return
    }
    // Failed cloud save: keep everything intact and offer an explicit retry.
    setSaveState(result.state)
    setSaveError(result.error)
  }

  const requestClose = () => {
    if (saving) return
    if (dirty) {
      setDiscardOpen(true)
    } else {
      onCancel()
    }
  }

  const handleConfirmDiscard = () => {
    setDiscardOpen(false)
    onCancel()
  }

  const title =
    target.kind === 'new'
      ? 'Nieuwe notitie'
      : target.kind === 'edit'
        ? 'Notitie bewerken'
        : 'Concept verder bewerken'

  const saveLabel = saveState === 'sync_failed' || saveState === 'sync_queued' ? 'Opnieuw proberen' : 'Opslaan'

  return (
    <>
      <BottomSheet
        open={open}
        onClose={requestClose}
        title={title}
        footer={
          <SaveBar
            visible
            saving={saving}
            saveLabel={saveLabel}
            onSave={() => {
              void handleSave()
            }}
            onCancel={requestClose}
            state={saving ? 'syncing' : saveState}
          />
        }
      >
        <div className="reminders-form">
          {saveError ? (
            <Banner
              tone="error"
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void handleSave()
                  }}
                  disabled={saving}
                >
                  Opnieuw proberen
                </Button>
              }
            >
              {saveError.userMessage} Je gegevens zijn bewaard op dit apparaat.
            </Banner>
          ) : null}

          <TextField
            label="Titel"
            value={values.title}
            onChange={(title) => updateValue({ title })}
            error={titleError ?? undefined}
            required
            placeholder="Waar moet je aan denken?"
          />

          <Select
            label="Categorie"
            value={values.category}
            onChange={(category) => updateValue({ category })}
            options={NOTE_CATEGORIES.map((category) => ({ value: category, label: category }))}
          />

          <TextArea
            label="Notitie"
            value={values.content}
            onChange={(content) => updateValue({ content })}
            rows={5}
            placeholder="Extra details om te onthouden"
          />

          <Checkbox
            label="Zet vast bovenaan de lijst"
            checked={values.pinned}
            onChange={(pinned) => updateValue({ pinned })}
          />
          <Checkbox
            label="Markeer als urgent"
            checked={values.urgent}
            onChange={(urgent) => updateValue({ urgent })}
          />

          {dirty ? (
            <p className="reminders-draft-note">
              Concept wordt automatisch op dit apparaat bewaard.{' '}
              <SyncStatus state={saveState ?? 'local_saved'} />
            </p>
          ) : null}
        </div>
      </BottomSheet>

      {/* Route-leave guard while the sheet is dirty */}
      <ConfirmDestructiveAction
        open={blocker.state === 'blocked'}
        title="Wijzigingen nog niet opgeslagen in de cloud"
        body="Je wijzigingen blijven als concept op dit apparaat bewaard. Wil je deze pagina toch verlaten?"
        confirmLabel="Pagina verlaten"
        onConfirm={() => {
          onPersistDraft(buildDraft('local_saved'))
          blocker.proceed?.()
        }}
        onCancel={() => blocker.reset?.()}
      />

      {/* Explicit discard confirm for cancel/close on a dirty sheet */}
      <ConfirmDestructiveAction
        open={discardOpen}
        title="Wijzigingen verwerpen?"
        body="Je niet-opgeslagen wijzigingen en het concept op dit apparaat worden verwijderd."
        confirmLabel="Verwerpen"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  )
}
